import React from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL + '/api';

/**
 * ErrorBoundary
 *
 * Catches render errors anywhere in its child tree so a single bad component
 * doesn't white-screen the whole SPA. Renders a friendly fallback UI with
 * Reload and Back-to-Dashboard recovery actions, and attempts to ship the
 * error to the backend logs API so crashes surface in the Events feed.
 *
 * Usage:
 *   <ErrorBoundary>            // top-level catch-all
 *     <Routes>...</Routes>
 *   </ErrorBoundary>
 *
 *   <ErrorBoundary scope="Holdings">  // per-route isolation
 *     <HoldingsPage />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.handleReload = this.handleReload.bind(this);
    this.handleBackToDashboard = this.handleBackToDashboard.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Always log to the console so devs see the full stack locally.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Caught render error:', error, errorInfo);

    // Best-effort report to backend Events feed. Swallow any failure — the
    // logging call must never re-crash the fallback UI (e.g. unauthenticated
    // users or non-admins in production will 401/403, which is expected).
    try {
      const scope = this.props.scope || 'app';
      const description = `Render error in ${scope}: ${error?.message || 'Unknown error'}`;
      const metadata = {
        scope,
        message: error?.message || null,
        stack: error?.stack ? String(error.stack).slice(0, 4000) : null,
        componentStack: errorInfo?.componentStack
          ? String(errorInfo.componentStack).slice(0, 4000)
          : null,
        url: typeof window !== 'undefined' ? window.location?.href : null,
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : null,
      };

      axios
        .post(
          `${API_BASE_URL}/logs`,
          {
            eventType: 'frontend.render_error',
            description,
            severity: 'error',
            metadata,
          },
          { withCredentials: true }
        )
        .catch(() => {
          // Intentionally ignored: the fallback UI must remain stable even if
          // the logs endpoint is unreachable or rejects the request.
        });
    } catch (e) {
      // Defensive: don't let the logger throw and re-trigger the boundary.
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary] Failed to report error:', e);
    }
  }

  handleReload() {
    if (typeof window !== 'undefined' && window.location) {
      window.location.reload();
    }
  }

  handleBackToDashboard() {
    if (typeof window !== 'undefined' && window.location) {
      // Hard navigation guarantees we unmount the broken tree and reinitialize
      // React state, which is safer than relying on the router from inside a
      // potentially-corrupt render context.
      window.location.assign('/');
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, errorInfo } = this.state;
    const scope = this.props.scope || null;
    const isDev = process.env.NODE_ENV !== 'production';

    return (
      <div
        role="alert"
        aria-live="assertive"
        style={{
          maxWidth: 640,
          margin: '64px auto',
          padding: '24px 28px',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          color: '#1f2937',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
        }}
      >
        <h1 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 600 }}>
          Something went wrong
        </h1>
        <p style={{ margin: '0 0 16px', color: '#4b5563', lineHeight: 1.5 }}>
          {scope
            ? `The ${scope} view hit an unexpected error and couldn't be displayed.`
            : 'The app hit an unexpected error and couldn’t be displayed.'}{' '}
          The error has been reported. You can try reloading or go back to the
          dashboard.
        </p>

        {error?.message ? (
          <pre
            style={{
              margin: '0 0 16px',
              padding: '10px 12px',
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              color: '#b91c1c',
              fontSize: 13,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {error.message}
          </pre>
        ) : null}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: '8px 14px',
              fontSize: 14,
              fontWeight: 500,
              color: '#fff',
              background: '#2563eb',
              border: '1px solid #2563eb',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Reload
          </button>
          <button
            type="button"
            onClick={this.handleBackToDashboard}
            style={{
              padding: '8px 14px',
              fontSize: 14,
              fontWeight: 500,
              color: '#1f2937',
              background: '#fff',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Back to Dashboard
          </button>
        </div>

        {isDev && errorInfo?.componentStack ? (
          <details style={{ marginTop: 16 }}>
            <summary style={{ cursor: 'pointer', color: '#6b7280' }}>
              Component stack (dev only)
            </summary>
            <pre
              style={{
                marginTop: 8,
                padding: '10px 12px',
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                fontSize: 12,
                color: '#374151',
                whiteSpace: 'pre-wrap',
                overflowX: 'auto',
              }}
            >
              {errorInfo.componentStack}
            </pre>
          </details>
        ) : null}
      </div>
    );
  }
}

export default ErrorBoundary;
